// src/encounter/ips-immunization-dialog.tsx
// Add/Edit dialog for the IPS-rail Immunizations/Vaccines section (PROD-1267). PRESENTATIONAL —
// composes DS Modal + CodeSearchField (CVX vaccine) + DatePicker (administered date) + TextField
// (optional remarks) + Button. No hooks/network: the host supplies search results + the
// submit/search callbacks (mirrors IpsProcedureDialog / IpsConditionDialog). IPS-FAITHFUL: only
// vaccine + administered date + remarks (the Flutter IPS add deliberately strips lot/expiry/site/
// dose). On submit it resolves the selection into an ImmunizationFormValue (vaccine + administered
// → administratedDate ISO-no-Z + administratedDisplayDate, typo preserved); the rail turns that
// into a wire request via buildImmunizationRequest + useSaveImmunization.
import { useState } from 'react';
import {
  Button,
  CodeSearchField,
  TextField,
  HelperText,
  type CodeSearchResult,
} from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';
import type { ImmunizationCoding, PatientImmunization } from '@/types/patient-chart-api';
import { DynamicDateInput } from '@/patients/components/chart/dynamic-date-input';
import type { ImmunizationFormValue } from '@/encounter/lib/to-immunization-request';
import { normalizeVaccineCoding } from '@/encounter/lib/to-immunization-request';

export interface IpsImmunizationDialogProps {
  open: boolean;
  /** Present → Edit mode; absent → Add mode. */
  editing?: PatientImmunization | null;

  // Vaccine search (host-owned).
  query: string;
  onQueryChange: (next: string) => void;
  results: CodeSearchResult[];
  searching?: boolean;
  /** Resolve a clicked result id → the full CVX ImmunizationCoding row (reads display). */
  resolveVaccine: (result: CodeSearchResult) => ImmunizationCoding | null;

  saving?: boolean;
  onSubmit: (value: ImmunizationFormValue) => void;
  onClose: () => void;
}

/** THE ruled field-label treatment (PROD-1801, Andrew 2 Aug) — shared, not re-declared.
 *  These six dialogs each carried a byte-identical local copy at xs/600/0.08em, which is a
 *  seventh idiom next to the four the rail popover already showed at once. The margin is the
 *  only local part: `AP_FIELD_LABEL` sets no spacing, and these labels sit above their field. */
// NOTE: AP_FIELD_LABEL lives in ap-dialog-label-style.tsx (out of scope for this pass), so its
// literal values (12/700/uppercase/0.01em/--color-text-hint) are reproduced here as the
// equivalent class string rather than imported as a style object.
const FIELD_LABEL =
  'pa-ips-label block text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint mb-1';
const FIELD_GROUP = 'mb-4';
const SELECTED_CHIP =
  'pa-ips-chip flex items-center justify-between gap-2 py-2 px-3 border border-solid border-border-default rounded-md bg-surface-card';
/** Vaccine display reads `display` (CVX), falling back through description / shortName / code. */
function vaccineLabel(c: ImmunizationCoding): string {
  return (c.display || c.description || c.shortName || c.code || '—').trim();
}

/** ISO 8601 WITHOUT the trailing Z — the wire convention (matches the procedure/condition dialogs). */
/** Seed the partial-date control, preferring the stored VERBATIM display.
 *
 *  Flutter's vaccination date is a DynamicDatePicker (`vax_tab.dart:531`), so "3y" / "MM/YYYY" /
 *  a bare year are all valid entries. The wire already carries the pair —
 *  `administratedDisplayDate` holds the verbatim text, `administratedDate` the resolved instant —
 *  so preferring the stored display makes "3y" round-trip as "3y" rather than being rewritten as
 *  whatever date it resolved to. */
function seedDate(existing: PatientImmunization | null | undefined): {
  displayValue: string;
  isoValue: string | null;
} {
  return {
    displayValue: existing?.administratedDisplayDate ?? '',
    isoValue: existing?.administratedDate ?? null,
  };
}

/** Outer gate + keyed remount; the inner form initialises state from `editing` via lazy
 *  useState initialisers (no setState-in-effect). */
export function IpsImmunizationDialog(props: IpsImmunizationDialogProps) {
  if (!props.open) return null;
  return <IpsImmunizationDialogForm key={props.editing?.identifier ?? 'add'} {...props} />;
}

function IpsImmunizationDialogForm({
  editing = null,
  query,
  onQueryChange,
  results,
  searching,
  resolveVaccine,
  saving,
  onSubmit,
  onClose,
}: IpsImmunizationDialogProps) {
  const isEditing = !!editing?.identifier;
  const [vaccine, setVaccine] = useState<ImmunizationCoding | null>(() =>
    normalizeVaccineCoding(editing?.vaccine),
  );
  const [date, setDate] = useState(() => seedDate(editing));
  const [remarks, setRemarks] = useState<string>(() => editing?.remarks ?? '');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    if (!vaccine) {
      setError('Pick a vaccine.');
      return;
    }
    // Typed-but-unparseable is a different error from empty — say which, or the user cannot tell
    // whether the field is required or their input was rejected.
    if (!date.isoValue) {
      setError(
        date.displayValue.trim()
          ? 'That date could not be read. Try DD/MM/YYYY, MM/YYYY, YYYY, or a duration like 3Y.'
          : 'Enter an administered date.',
      );
      return;
    }
    onSubmit({
      vaccine,
      administratedDate: date.isoValue,
      administratedDisplayDate: date.displayValue.trim() || null,
      remarks: remarks.trim() || null,
    });
  }

  return (
    <AddSurface
      open
      onClose={onClose}
      title={isEditing ? 'Edit vaccine' : 'Add vaccine'}
      size="md"
      commit={{
        run: handleSubmit,
        label: 'Save vaccination',
        text: saving ? 'Saving…' : isEditing ? 'Save' : 'Add',
        busy: saving,
      }}
    >
      <div className="font-sans">
        {/* Vaccine (CVX) */}
        <div className={FIELD_GROUP}>
          <span className={FIELD_LABEL}>Vaccine</span>
          {vaccine ? (
            <div className={SELECTED_CHIP}>
              <span className="text-size-sm text-text-primary">
                {vaccineLabel(vaccine)}
                {vaccine.code ? (
                  <span className="text-text-hint"> · CVX {vaccine.code}</span>
                ) : null}
              </span>
              <Button variant="tertiary" size="sm" onClick={() => setVaccine(null)} disabled={saving}>
                Change
              </Button>
            </div>
          ) : (
            <CodeSearchField
              value={query}
              onChange={onQueryChange}
              results={results}
              loading={searching}
              placeholder="Search vaccines (e.g. covid, influenza)…"
              onResultClick={(r) => {
                const picked = resolveVaccine(r);
                if (picked) {
                  setVaccine(picked);
                  setError(null);
                }
              }}
            />
          )}
        </div>

        {/* Administered date */}
        <div className={FIELD_GROUP}>
          <DynamicDateInput
            label="Administered date"
            required
            displayValue={date.displayValue}
            isoValue={date.isoValue}
            onChange={setDate}
            disabled={saving}
          />
        </div>

        {/* Remarks (optional) */}
        <div className={FIELD_GROUP}>
          <TextField
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Optional context, e.g. reaction notes…"
            disabled={saving}
          />
        </div>

        {error ? <HelperText tone="error">{error}</HelperText> : null}
      </div>
    </AddSurface>
  );
}
